import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactCTA from '../components/ContactCTA';
import { Award, Snowflake, Fish, Truck, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { companySchema, getPageMetadata, SITE } from '../config/seo';

const AwardsPage = () => {
  const { language } = useLanguage();

  const getLocalizedText = (texts) => texts[language] || texts.tc;
  const meta = getPageMetadata('awards', language);
  const schemas = [
    companySchema(),
    { '@type': 'WebPage', name: meta.title, url: `${SITE.url}/awards`, about: { '@id': `${SITE.url}/#organization` } },
  ];

  return (
    <div className="min-h-screen">
      <SEO {...meta} path="/awards" language={language} schemas={schemas} />
      <Header />

      <section className="py-20 bg-gradient-to-br from-cyan-50 to-white" data-testid="advantages-section">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl font-bold mb-6 gradient-text" data-testid="advantages-title">
              {language === 'tc' ? '我們優勢' : language === 'cn' ? '我们优势' : 'Our Advantages'}
            </h1>
            <p className="text-xl text-slate-600">
              {language === 'tc' 
                ? '頂尖技術設備，為您提供最優質的海鮮產品和服務'
                : language === 'cn'
                ? '顶尖技术设备，为您提供最优质的海鲜产品和服务'
                : 'State-of-the-art equipment delivering premium seafood products and services'}
            </p>
          </motion.div>

          {/* Transport Fleet Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass p-8 rounded-3xl mb-16"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white">
                <Truck className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-bold">
                {language === 'tc' ? '運輸車隊' : language === 'cn' ? '运输车队' : 'Transport Fleet'}
              </h2>
            </div>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              {language === 'tc'
                ? '我們的運輸車隊配備了專業的冷藏運輸車輛，確保產品在運送過程中始終保持最佳的冷藏狀態。我們的司機經過專業培訓，能夠準時將新鮮海產安全送達每一位客戶，為您的業務提供可靠的物流支持。'
                : language === 'cn'
                ? '我们的运输车队配备了专业的冷藏运输车辆，确保产品在运送过程中始终保持最佳的冷藏状态。我们的司机经过专业培训，能够准时将新鲜海产安全送达每一位客户，为您的业务提供可靠的物流支持。'
                : 'Our transport fleet is equipped with professional refrigerated vehicles, ensuring products maintain optimal cold storage conditions throughout delivery. Our professionally trained drivers deliver fresh seafood safely and punctually to every customer, providing reliable logistics support for your business.'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_webpagechat/artifacts/jd99cfqs_001.jpeg"
                  alt="Transport Fleet 1"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/7lgk302s_img_005.jpeg"
                  alt="Transport Fleet 2"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Ice Equipment Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass p-8 rounded-3xl mb-16"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white">
                <Snowflake className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-bold">
                {language === 'tc' ? '冷凍設備，縮短廚師等待時間' : language === 'cn' ? '冷冻设备，缩短厨师等待时间' : 'Refrigeration Equipment - Reducing Chef Wait Time'}
              </h2>
            </div>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              {language === 'tc'
                ? '我們公司設有多個專業冷凍設施設備，它的要求和設計十分重要，有急凍冷藏效果，更關乎操作安全以及食品安全。冷凍倉庫設計：公司已確保倉庫的溫度控制系統，並配備高效節能的設備，同時重視空間的合理規劃以便提高儲存和運輸效率。 我們的冷凍系統採用冷鏈技術，從採購到交付客戶，確保每一個環節都以高標準冷藏運行。還配備了大型水冷制冰機， 每天24小時運作， 用香港優質供水再會經過食用過濾芯制作冰粒，有效地減低食材細菌生長。我們注重食安冷凍及安全意識。這意味著現代廚師不僅能信賴我們的產品至到品質，更能依賴我們準時交付的承諾。'
                : language === 'cn'
                ? '我们公司设有多个专业冷冻设施设备，它的要求和设计十分重要，有急冻冷藏效果，更关乎操作安全以及食品安全。冷冻仓库设计：公司已确保仓库的温度控制系统，并配备高效节能的设备，同时重视空间的合理规划以便提高储存和运输效率。 我们的冷冻系统采用冷链技术，从采购到交付客户，确保每一个环节都以高标准冷藏运行。还配备了大型水冷制冰机， 每天24小时运作， 用香港优质供水再会经过食用过滤芯制作冰粒，有效地减低食材细菌生长。我们注重食安冷冻及安全意识。这意味着现代厨师不仅能信赖我们的产品至到品质，更能依赖我们准时交付的承诺。'
                : 'Our company has multiple professional refrigeration facilities. Their requirements and design are crucial, providing fast freezing and cold storage effects while ensuring operational safety and food safety. Warehouse Design: We ensure warehouse temperature control systems equipped with energy-efficient equipment, emphasizing rational space planning to improve storage and transportation efficiency. Our refrigeration system uses cold chain technology, ensuring every step from procurement to customer delivery operates at high refrigeration standards. We are also equipped with large-scale water-cooled ice machines operating 24 hours daily, using Hong Kong\'s premium water supply filtered through food-grade filters to make ice, effectively reducing bacterial growth in ingredients. We emphasize food safety, refrigeration, and safety awareness. This means modern chefs can not only trust our product quality but also rely on our commitment to timely delivery.'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/2l1tzedh_img_004.jpeg"
                  alt="Refrigeration Equipment 1"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/zyt0on0i_002.jpeg"
                  alt="Ice Machine 2"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/zcyx9e3n_003.jpeg"
                  alt="Ice Machine 3"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Aquaculture Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass p-8 rounded-3xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white">
                <Fish className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-bold">
                {language === 'tc' ? '專業養殖池設施' : language === 'cn' ? '专业养殖池设施' : 'AQUACULTURE FACILITIES'}
              </h2>
            </div>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              {language === 'tc'
                ? '專業的低溫池，確保由海外入口的鮮活海產，任何時間都能隨時買到最鮮活的海產品。先進水質監測，魚池配合強大的活性炭細菌過濾及氮氣消毒處理，保持優良水質，令生猛海鮮肉質更鮮甜美味。'
                : language === 'cn'
                ? '专业的低温池，确保由海外入口的鲜活海产，任何时间都能随时买到最鲜活的海产品。先进水质监测，鱼池配合强大的活性炭细菌过滤及氮气消毒处理，保持优良水质，令生猛海鲜肉质更鲜甜美味。'
                : 'Our professional aquaculture facilities feature specialized low-temperature pools designed to ensure the availability of the freshest live seafood imported from overseas at any time. Advanced water quality monitoring systems support our fish tanks, utilizing powerful activated carbon filtration and nitrogen-based sterilization to maintain superior water conditions. This meticulous process enhances the freshness and flavor of live seafood, resulting in a richer, sweeter taste to the customers.'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/adw4ozvk_007.jpeg"
                  alt="Aquaculture 1"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/v6anndmr_006.jpeg"
                  alt="Aquaculture 2"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/q85qv9dt_008.jpeg"
                  alt="Aquaculture 3"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Manual Processing Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass p-8 rounded-3xl mt-16"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center text-white">
                <Users className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-bold">
                {language === 'tc' ? '人手加工及后勤配合' : language === 'cn' ? '人手加工及后勤配合' : 'Manual Processing and Logistics Support'}
              </h2>
            </div>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-8">
              {language === 'tc'
                ? '我們擁有經驗豐富的加工團隊，專業進行各類海產的手工處理，確保產品的質量和新鮮度。我們注重後勤配合，確保從加工到配送的每一步環節流暢高效，以滿足客戶的需求。'
                : language === 'cn'
                ? '我们拥有经验丰富的加工团队，专业进行各类海产的手工处理，确保产品的质量和新鲜度。我们注重后勤配合，确保从加工到配送的每一步环节流畅高效，以满足客户的需求。'
                : 'We have an experienced processing team that professionally handles all types of seafood manually, ensuring product quality and freshness. We emphasize logistics coordination, ensuring smooth and efficient operations from processing to delivery to meet customer needs.'}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/0yyn7g0v_004.jpeg"
                  alt="Manual Processing 1"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_seafood-cms/artifacts/zn4rvmbf_005.jpeg"
                  alt="Manual Processing 2"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Future Goals */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto glass p-12 rounded-3xl"
          >
            <h2 className="text-4xl font-bold mb-6 gradient-text">
              {language === 'tc' ? '未來目標' : language === 'cn' ? '未来目标' : 'Future Goals'}
            </h2>
            <p className="text-xl leading-relaxed text-slate-700">
              {language === 'tc'
                ? '為提升附加值和社會責任，樹記海鮮正積極探索與第三方機構合作，尋求本地有機認證和ESG可持續認證。'
                : language === 'cn'
                ? '为提升附加值和社会责任，树记海鲜正积极探索与第三方机构合作，寻求本地有机认证和ESG可持续认证。'
                : 'To enhance added value and social responsibility, Shu Kee Seafood is actively exploring collaborations with third-party organizations for local organic certification and ESG sustainable certification.'}
            </p>
          </motion.div>
        </div>
      </section>

      <ContactCTA />

      <Footer />
    </div>
  );
};

export default AwardsPage;